import { Component, OnInit, inject } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { AgentAccessService, AgentModelChoice } from '../../services/agent-access.service';

/** Value of the "no own choice" option — nb-select treats a `null` option as "reset". */
const DEFAULT = '';

/**
 * Einstellungen › Portal-Assistent: which model the portal assistant runs on.
 * The organisation (admins/managers) picks one and may let people pick their
 * own. Only models fit for the assistant's work are offered (al-tutor
 * `portal_agent_model`, same rule checked again when a turn starts); the
 * credit factor stands next to each so the cost is visible.
 */
@Component({
  selector: 'app-agent-model-panel',
  templateUrl: './agent-model-panel.component.html',
  styleUrls: ['./agent-model-panel.component.scss'],
})
export class AgentModelPanelComponent implements OnInit {
  private readonly access = inject(AgentAccessService);
  private readonly toastr = inject(NbToastrService);
  private readonly translate = inject(TranslateService);

  readonly none = DEFAULT;
  choice: AgentModelChoice | null = null;
  loading = true;
  savingOrg = false;
  savingMine = false;

  orgProvider = DEFAULT;
  allowUsers = false;
  myProvider = DEFAULT;

  ngOnInit(): void {
    this.access
      .getModel()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (choice) => this.apply(choice),
        error: () => this.toastr.danger(this.translate.instant('pages.settings.agentModel.errors.load'), ''),
      });
  }

  get orgDirty(): boolean {
    const org = this.choice?.organisation;
    return !!org && (this.orgProvider !== (org.provider ?? DEFAULT) || this.allowUsers !== org.allow_user_choice);
  }

  get myDirty(): boolean {
    return !!this.choice && this.myProvider !== (this.choice.me.provider ?? DEFAULT);
  }

  saveOrganisation(): void {
    if (!this.orgDirty || this.savingOrg) return;
    this.savingOrg = true;
    this.access
      .setOrganisationModel(this.orgProvider || null, this.allowUsers)
      .pipe(finalize(() => (this.savingOrg = false)))
      .subscribe({
        next: (choice) => {
          this.apply(choice);
          this.toastr.success(this.translate.instant('pages.settings.agentModel.saved'), '');
        },
        error: () => this.toastr.danger(this.translate.instant('pages.settings.agentModel.errors.save'), ''),
      });
  }

  saveMine(): void {
    if (!this.myDirty || this.savingMine) return;
    this.savingMine = true;
    this.access
      .setMyModel(this.myProvider || null)
      .pipe(finalize(() => (this.savingMine = false)))
      .subscribe({
        next: (choice) => {
          this.apply(choice);
          this.toastr.success(this.translate.instant('pages.settings.agentModel.saved'), '');
        },
        error: () => this.toastr.danger(this.translate.instant('pages.settings.agentModel.errors.save'), ''),
      });
  }

  private apply(choice: AgentModelChoice): void {
    this.choice = choice;
    this.orgProvider = choice.organisation.provider ?? DEFAULT;
    this.allowUsers = choice.organisation.allow_user_choice;
    this.myProvider = choice.me.provider ?? DEFAULT;
  }
}
